import { WorkOrderPermissions } from '@zspace/roles';
import {
  PaginatedAPIResponse,
  SortDirection,
  WorkOrderDashboardPageTabs,
  WorkOrderStatus,
  WorkOrderSummaryData,
  WorkOrderType,
  WorkOrdersCriteria,
} from '@zspace/types';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaPlus } from 'react-icons/fa6';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigation,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import Button from '../../ui/button/button';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import Spinner from '../../ui/spinner/spinner';
import { WorkOrderDashboardPageContext } from '../work-orders-dashboard-page/types';
import { fetchWorkOrders } from '../work-orders-service';
import WorkOrdersTableCard from '../work-orders-table-card/work-orders-table-card';

const initialCriteriaData = (searchParams?: URLSearchParams) => ({
  itemsPerPage: parseInt(
    getSearchParamValue(searchParams, 'itemsPerPage') ?? '10'
  ),
  pageNumber: parseInt(getSearchParamValue(searchParams, 'pageNumber') ?? '1'),
  search: getSearchParamValue(searchParams, 'search') ?? '',
  sortBy: getSearchParamValue(searchParams, 'sortBy') ?? 'createdAt',
  sortDirection:
    getSearchParamValue<SortDirection>(searchParams, 'sortDirection') ??
    SortDirection.DES,
  workOrderName: getSearchParamValues(searchParams, 'workOrderName'),
  accounts: getSearchParamValues(searchParams, 'accounts'),
  type: getSearchParamValues<WorkOrderType>(searchParams, 'type'),
  createdBy: getSearchParamValues(searchParams, 'createdBy'),
  salesOrders: getSearchParamValues(searchParams, 'salesOrders'),
  status: WorkOrderStatus.DRAFT,
});

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);
  const criteria = initialCriteriaData(searchParams);

  const response = fetchWorkOrders(criteria);

  return defer({ response });
};

function WorkOrdersDraftsTabContent() {
  const {
    setActiveTab,
    setShowCreateWorkOrderButton,
    handleCreateWorkOrderButtonClick,
  } = useOutletContext<WorkOrderDashboardPageContext>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();

  const response =
    useAsyncValue() as PaginatedAPIResponse<WorkOrderSummaryData>;

  const [data, setData] = useState<WorkOrdersCriteria>(
    initialCriteriaData(searchParams)
  );

  const emptyTableContent = (
    <>
      <Element display="flex" justifyContent="center">
        <span className="my-2 has-text-weight-light">
          There are no work orders drafts at the moment
        </span>
      </Element>
      <CheckPermissions permissions={WorkOrderPermissions.WORK_ORDERS_CREATE}>
        <CheckPermissions.Render>
          <Element display="flex" justifyContent="center">
            <Button
              color="primary-dark"
              onClick={handleCreateWorkOrderButtonClick}
            >
              <Icon>
                <FaPlus />
              </Icon>
              <span>Create work order</span>
            </Button>
          </Element>
        </CheckPermissions.Render>
      </CheckPermissions>
    </>
  );

  const emptyTableContentWithFilters = (
    <Element display="flex" justifyContent="center">
      <span className="my-2">
        There are no work orders drafts that match the selected criteria
      </span>
    </Element>
  );

  const isDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  useEffect(() => {
    setActiveTab(WorkOrderDashboardPageTabs.DRAFTS);
  }, [setActiveTab]);

  useEffect(() => {
    setShowCreateWorkOrderButton(response?.data?.length > 0);
  }, [response?.data?.length, setShowCreateWorkOrderButton]);

  if (isDataLoading) {
    return (
      <Element display="flex" justifyContent="center">
        <Spinner />
      </Element>
    );
  }

  return (
    <WorkOrdersTableCard
      initialCriteria={initialCriteriaData()}
      criteria={data}
      setCriteria={setData}
      tableData={response}
      workOrdersStatus={WorkOrderStatus.DRAFT}
      emptyTableContent={emptyTableContent}
      emptyTableContentWithFilters={emptyTableContentWithFilters}
    />
  );
}

export function WorkOrdersDraftsTab() {
  const { response } = useLoaderData() as {
    response: Promise<PaginatedAPIResponse<WorkOrderSummaryData>>;
  };

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <WorkOrdersDraftsTabContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default WorkOrdersDraftsTab;
